import { all, put, takeLatest, call } from 'redux-saga/effects';
import { PayloadAction } from '@reduxjs/toolkit';
import { CONFIG } from '~/config';
import { convertToPriceCheckResponse } from '~/modules/helpers';
import { ExternalRestClient } from '~/modules/requests';
import {
  getFlightOfferBookingDetails,
  getFlightOfferBookingDetailsSuccess,
  getFlightOfferBookingDetailsFailure,
  requestPayment,
  resetOfferSearchData,
  getUserReservations,
  getUserReservationsSuccess,
  getUserReservationsFailed,
  logOut,
} from '~/actions';
import { ERROR_MESSAGE } from '~/literals';
import { ApiResponse, FlightBookingState, FlightOfferPriceCheckResponse, FlightReservation, PNRRecord, ReservationEmailParams } from '~/types';
import {
  GetFlightsBookingQuery,
  getReservation,
  getReservationFailed,
  getReservationSuccess,
  requestPaymentFail,
  requestPaymentSuccess,
  sendReservationEmail,
  sendReservationEmailReservationFailed,
  sendReservationEmailReservationSuccess,
  setLoginRequired,
} from '~/store/slices/flight-booking';
import { ERROR_RESPONSE_STATUS } from '~/literals';

export function* getConfirmedOfferPriceAndBookingDetailsSaga({ payload }: PayloadAction<GetFlightsBookingQuery>) {
  const url = `${CONFIG.env.apiBaseUrl}v1/flight-booking/confirm-offer-price/${payload.id}`;
  try {
    const response: ApiResponse<any> = yield call(
      [ExternalRestClient, ExternalRestClient.get],
      url
    );

    if (response.status === 200 && response.data) {
      const converted: FlightOfferPriceCheckResponse = yield call(convertToPriceCheckResponse, response.data);
      yield put(getFlightOfferBookingDetailsSuccess(converted));
    } else {
      const errorMsg = response.data?.error || ERROR_MESSAGE.UNKOWN_ERROR;
      yield put(getFlightOfferBookingDetailsFailure({ errorMessage: errorMsg }));
    }
  } catch (error: any) {
    const msg = error.status === 404 ? ERROR_MESSAGE.PAGE_NOT_FOUND : ERROR_MESSAGE.UNKOWN_ERROR;
    yield put(getFlightOfferBookingDetailsFailure({ errorMessage: msg }));
  }
}

// ... rest of sagas unchanged ...
export function* requestPaymentSaga({ payload }: PayloadAction<FlightBookingState>) {
  const orderData = {
    source: payload.offerBookingDetails?.response.source,
    bookingContext: payload.offerBookingDetails?.bookingContext,
    host: window.location.origin,
    order: {
      offers: payload.flightPostBookingParameters?.order?.offers,
      passengers: payload.flightPostBookingParameters?.passengers,
      remarks: payload.flightPostBookingParameters?.remarks,
      ticketingAgreement: payload.flightPostBookingParameters?.ticketingAgreement,
      tripContactPersons: payload.flightPostBookingParameters?.tripContactPersons,
      termsAccepted: payload.flightPostBookingParameters?.termsAccepted,
    },
  }

  try {
    const response: ApiResponse<any> = yield ExternalRestClient.post(
      `${CONFIG.env.apiBaseUrl}v1/flight-booking/create-booking/${payload.offerBookingDetails?.priceCheckId}`,
      orderData
    );

    if (response.status === 200) {
      if (response.data) {
        yield put(resetOfferSearchData())
        yield put(requestPaymentSuccess(response.data));
      }
    } else {
      yield put(resetOfferSearchData())
      yield put(requestPaymentFail(
        {
          errorMessage: `${response.data.error}`,
        }
      ));
    }
  } catch (error: any) {

    yield put(resetOfferSearchData())
    const { response } = error;
    if (response && response.status === 405) {
      const errorMessage = response.data.error;
      if (errorMessage === ERROR_RESPONSE_STATUS.PRICE_CHANGED) {
        yield put(requestPaymentFail(
          {
            errorMessage: ERROR_MESSAGE.PRICE_CHANGED,
          }
        ));
      } else {
        yield put(requestPaymentFail(
          {
            errorMessage: ERROR_MESSAGE.UNKOWN_ERROR,
          }
        ));
      }
    }
    else {

      yield put(requestPaymentFail(
        {
          errorMessage: `Failed to request for payment. An error occurred. ${error.data}`,
        }
      ));
    }

  }
}

function getReservationByBookingId(payload: string) {
  return ExternalRestClient.get(
    `${CONFIG.env.apiBaseUrl}v1/flight-booking/reservation/${payload}`,
  );
}

function delayEffect(duration: number) {
  return new Promise(resolve => setTimeout(resolve, duration));
}

export function* getReservationSaga({ payload }: PayloadAction<string>) {
  const maxRetryTime = 45000;  // 45 seconds max retry time
  let delay = 2000;            // initial delay of 2 seconds
  let totalTime = 0;           // track total retry time

  try {
    let response: ApiResponse<any>;

    while (totalTime < maxRetryTime) {
      try {
        response = yield call(getReservationByBookingId, payload);

        if (response.status === 200) {
          if (response.data) {
            const reservation: FlightReservation = {
              PNRRecords: response.data.PNRRecords.map((item: PNRRecord) => ({
                ...item,
              })),
              bookingDetails: {
                offers: response.data.booking.order.offers,
                passengers: response.data.booking.order.passengers,
                tripContactPersons: response.data.booking.order.tripContactPersons,
                agentFees: {
                  fees: [],
                  total: response.data.booking.totalAgentFees,
                }
              },
              localPNRCode: response.data.localPNRCode,
              reservationId: response.data._id,
              bookingContext: response.data.bookingContext,
            };
            yield put(getReservationSuccess(reservation));
            return;
          }
        } else if (response.status === 404) {
          throw new Error('Not Found');
        } else {
          yield put(getReservationFailed({
            errorMessage: 'Something went wrong with your reservation. Please try again later. If the issue persists, contact support for assistance.',
          }));
          return;
        }

      } catch (error: any) {
        if (error?.message === 'Not Found') {
          throw error;
        }

        if (error?.response?.status !== 404) {
          throw error;
        }

        yield call(delayEffect, delay);
        totalTime += delay;
        delay *= 2;
      }
    }

    yield put(getReservationFailed({
      errorMessage: `Something went wrong with your reservation. We could not find your reservation, please contact support for assistance with reference ${payload}.`,
    }));

  } catch (error: any) {
    if (error?.response?.status === 422) {

      const { response } = error;

      const paymentErrors = response.data.filter((err: any) => err.status == ERROR_RESPONSE_STATUS.PAYMENT_FAILED);

      if (paymentErrors.length) {
        yield put(getReservationFailed({
          errorMessage: `Unfortunately, we were unable to process your payment. Please restart your search and try again. Reference ${payload}`,
        }));
      }

      const gdsPriceErrors = response.data.filter(
        (err: any) => err.status == ERROR_RESPONSE_STATUS.GDS_PRICE_CHANGED
      );

      if (gdsPriceErrors.length) {
        yield put(getReservationFailed({
          errorMessage: `We apologize, but the price of your flight changed before we received your payment. Any charges made to your card will be fully refunded. Reference ${payload}`,
        }));
      }

      const gdsReservationErrors = response.data.filter(
        (err: any) => err.status == ERROR_RESPONSE_STATUS.GDS_CREATE_ORDER_FAILURE ||
          err.status == ERROR_RESPONSE_STATUS.PAYMENT_REFUNDED
      );

      if (gdsReservationErrors.length) {
        yield put(getReservationFailed({
          errorMessage: `Unfortunately, we were unable to complete your reservation. Any charges made to your card will be fully refunded. Reference ${payload}`,
        }));
      }

      if (!response.data.length) {
        yield put(getReservationFailed({
          errorMessage: `Something went wrong with your reservation. We could not find your reservation, please contact support for assistance with reference ${payload}.`,
        }));
      }

      else {
        yield put(getReservationFailed({
          errorMessage: `Something went wrong with your reservation. Please try again later. If the issue persists, contact support for assistance with reference ${payload}`,
        }));
      }

    } else {
      yield put(getReservationFailed({
        errorMessage: `Something went wrong with your reservation. Please try again later. If the issue persists, contact support for assistance with reference ${payload}`,
      }));
    }
  }
}

export function* getUserReservationsSaga() {
  try {
    const response: ApiResponse<any> = yield ExternalRestClient.get(
      `${CONFIG.env.apiBaseUrl}v1/flight-booking/manage/reservation`,
    );

    if (response.status === 200) {
      if (response.data) {
        const reservations: FlightReservation[] = response.data.flightReservations ?
          response.data.flightReservations.map((reservation: any) => (
            {
              PNRRecords: reservation.PNRRecords.map((item: PNRRecord) => ({
                ...item,
              })),
              bookingDetails: {
                offers: reservation.booking?.order.offers,
                passengers: reservation.booking?.order.passengers,
                tripContactPersons: reservation.booking?.order.tripContactPersons,
                agentFees: {
                  fees: [],
                  total: reservation.booking?.totalAgentFees,
                }
              },
              localPNRCode: reservation.localPNRCode,
              reservationId: reservation._id,
              bookingContext: reservation.bookingContext,
            }
          )) : [];

        yield put(getUserReservationsSuccess(reservations));
      }
    } else {
      yield put(getUserReservationsFailed(
        {
          errorMessage: `${response.data.error}`,
        }
      ));
    }
  } catch (error: any) {
    if (error?.response?.status === 403) {
      yield put(logOut());
      yield put(setLoginRequired(
        {
          errorMessage: "Login to view your trips.",
        }
      ));
    } else {
      yield put(
        getUserReservationsFailed(
          {
            errorMessage: error?.response?.data.error || 'Request failed. Please try again later.'
          }
        ));
    }
  }
}

export function* sendReservationEmailSaga({ payload }: PayloadAction<ReservationEmailParams>) {
  try {
    const response: ApiResponse<any> = yield ExternalRestClient.post(
      `${CONFIG.env.apiBaseUrl}v1/flight-booking/reservation/email/${payload.bookingId}`,
      {
        email: payload.email,
      }
    );

    if (response.status === 200) {
      yield put(sendReservationEmailReservationSuccess());
    } else {
      yield put(sendReservationEmailReservationFailed());
    }
  } catch {
    yield put(sendReservationEmailReservationFailed());
  }
}


export default function* root() {
  yield all([
    takeLatest(getFlightOfferBookingDetails.type, getConfirmedOfferPriceAndBookingDetailsSaga),
    takeLatest(requestPayment.type, requestPaymentSaga),
    takeLatest(getReservation.type, getReservationSaga),
    takeLatest(getUserReservations.type, getUserReservationsSaga),
    takeLatest(sendReservationEmail.type, sendReservationEmailSaga),
  ]);
}
